import { SIGNUP_COUPON_CODES } from 'constants/crossSell';

import { notifyError } from 'components/common/Toast/Toast';
import { MembershipPlanProps, PricePointProps } from 'components/crossSell/PlanType/planType.types';
import dayjs from 'dayjs';
import { DateFormat } from 'enums/dateFormats';
import { MessageType } from 'enums/messages';
import capitalize from 'lodash/capitalize';
import { SignupCouponCodes } from 'store/crossSell/crossSell.types';

import { PRESCRIPTION_NAME_REGEX } from './regExp';

export const addDashesToPhoneNumber = (phone: string) => {
  return phone
    ? `${phone.slice(1, 2)}-${phone.slice(2, 5)}-${phone.slice(5, 8)}-${phone.slice(8)}`
    : '';
};

export const getTaskStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'urgent':
      return 'text-red';
    case 'new':
      return 'text-secondary';
    case 'active':
      return 'text-yellow';
    case 'sent':
      return 'text-primary';
    case 'pending':
      return 'text-orange-500';
  }
};

export const getDefaultPricePoint = (pricePoints: PricePointProps[]): PricePointProps | undefined =>
  pricePoints?.find((item) => item.isDefault);

export const getPaymentAmount = (
  promoCode: SignupCouponCodes | '',
  plan?: MembershipPlanProps | null,
  pricePoint?: PricePointProps | null
) => {
  if (!plan || !pricePoint) {
    return {
      amountDue: '',
      discountAmount: '',
      discountType: '',
      discountValue: ''
    };
  }

  if (promoCode === '') {
    return {
      amountDue: pricePoint?.totalCost,
      discountAmount: '0',
      discountType: 'amount',
      discountValue: '0'
    };
  }
  const discountType = SIGNUP_COUPON_CODES[promoCode]?.amount ? 'amount' : 'percent';
  const discountValue =
    discountType === 'amount'
      ? SIGNUP_COUPON_CODES[promoCode]?.amount
      : // discount_type === 'percent'
        SIGNUP_COUPON_CODES[promoCode]?.percentage;

  if (!discountValue) {
    return {
      amountDue: '',
      discountAmount: '',
      discountType: '',
      discountValue: ''
    };
  }
  const initialCost = Number(pricePoint?.totalCost) ?? 49;
  const discountAmount =
    discountType === 'amount'
      ? Number(discountValue)
      : (initialCost * parseFloat(discountValue)) / 100;
  const amountDue =
    discountValue !== '0' ? (initialCost - discountAmount).toFixed(2) : String(initialCost);

  return {
    amountDue,
    discountAmount: discountAmount.toFixed(2),
    discountType,
    discountValue
  };
};

export const validation = (
  fieldName: string,
  regexRule?: RegExp | null,
  errorMessage?: string | null,
  isOptionalField?: boolean
) => {
  return {
    required: {
      message: `${fieldName} is required`,
      value: !isOptionalField
    },
    ...(regexRule && {
      pattern: {
        message: errorMessage || 'Please enter a valid data',
        value: regexRule
      }
    })
  };
};

export const getISOStringIncludingTimezone = (
  date: string | number | Date | dayjs.Dayjs,
  timeZone: string,
  dateFormat?: string
) => {
  return dayjs(date, dateFormat).tz(timeZone).format(DateFormat.YYYY_MM_DDTHH_mm_ss_ssZ);
};

export const isSafariBrowser = () => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator?.userAgent);
  return isSafari;
};

export const isFirefoxBrowser = () => {
  const isFirefox = /firefox|fxios/i.test(navigator?.userAgent);
  return isFirefox;
};

export const handleRequestCatch = (
  e: MessageEvent,
  alternativeMessage = 'Error, please try again later'
) => {
  if ('status' in e && e.status === 500) {
    return;
  }
  notifyError(e.data?.message || alternativeMessage);
};

export const getMessageType = (type: MessageType) => {
  return type === MessageType.Medical
    ? 'medical'
    : type === MessageType.Support
      ? 'support'
      : 'staff-notes';
};

export const getControlledSelectOptionsFromEnum = (enumObject: Record<string, string>) => {
  return Object.values(enumObject).map((value) => ({
    disabled: false,
    label: capitalize(value),
    value
  }));
};

export const extractAndFormatPrescriptionName = (prescriptionName: string): string => {
  const match = prescriptionName.match(PRESCRIPTION_NAME_REGEX)?.[0] || '';
  return match
    .split(/\s+/)
    .map((word) => capitalize(word))
    .join(' ');
};
